import useRolePermission from "entities/auth/hooks/useRolePermission";
import { useCreateFaqGroupMutation } from "entities/faqs/mutations/useCreateFaqGroupMutation";
import { useUpdateFaqGroupMutation } from "entities/faqs/mutations/useUpdateFaqGroupMutation";
import { useFaqGroupDetailsQuery } from "entities/faqs/queries/useFaqGroupDetailsQuery";
import { getFaqGroupsQueryData } from "entities/faqs/queries/useFaqGroupsQuery";
import { FaqGroupDataToCreate, FaqGroupStatus } from "entities/faqs/types";
import React, { ChangeEvent, FC, useEffect, useMemo, useState } from "react";
import useInputState, { InputHookResult } from "shared/hooks/useInputState";
import { Translations } from "shared/types/languages";
import { createContext } from "shared/utils/context";
import { useFaqGroupPage } from "../../../context";
import {
  FaqGroupDetailsProviderProps,
  FaqGroupDetailsTabs,
  FaqGroupDetailsType,
} from "./types";

const [Provider, useFaqGroupDetails] = createContext<FaqGroupDetailsType>({
  name: "faqGroupDetailsContext",
  hookName: "useFaqGroupDetails",
  providerName: "faqGroupDetailsProvider",
});

const FaqGroupDetailsProvider: FC<FaqGroupDetailsProviderProps> = ({
  children,
}) => {
  const { faqGroupDrawer } = useFaqGroupPage();
  const { allowed: hasReadAccess } = useRolePermission("faq:read");
  const { allowed: hasReadTranslationsAccess } = useRolePermission(
    "faq:translations:read",
  );
  const { drawerDetails } = faqGroupDrawer;
  const [faqGroupId, setFaqGroupId] = useState<string>();
  const [selectedTab, setSelectedTab] = useState(FaqGroupDetailsTabs.General);
  const groupTitle: InputHookResult = useInputState("groupTitle", "");
  const groupTitleValue = groupTitle?.input.value;
  const [errors, setErrors] = useState<string>("");

  const { data: faqGroupData = null, isLoading } = useFaqGroupDetailsQuery(
    drawerDetails || "",
  );

  const [translations, setTranslations] = useState<Translations | undefined>();

  const { mutate: updateFaqGroupDetails, isPending: isSubmittingFaqGroup } =
    useUpdateFaqGroupMutation();

  const { mutate: createFaqGroupDetails, isPending } =
    useCreateFaqGroupMutation();

  const faqGroups = getFaqGroupsQueryData();

  const setDefaultGroup = () => {
    groupTitle.setValue("");
  };

  const disabled = useMemo(
    () => !groupTitleValue || Boolean(groupTitle.input.errors.length),
    [groupTitle.input.errors.length, groupTitleValue],
  );

  useEffect(() => {
    if (faqGroupData) {
      setFaqGroupId(faqGroupData?.id);
      groupTitle.setValue(faqGroupData.title);
      setTranslations(faqGroupData.translations);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [faqGroupData]);

  useEffect(() => {
    if (hasReadAccess) {
      return;
    }

    if (hasReadTranslationsAccess) {
      setSelectedTab(FaqGroupDetailsTabs.Translations);
    }
  }, [hasReadAccess, hasReadTranslationsAccess, faqGroupData?.id]);

  const performGroupTitleError = () => {
    return groupTitle?.setErrors([
      { key: "groupTitle", message: "This group title is already exists" },
    ]);
  };

  const onChangeGroupTitle = (e: ChangeEvent<HTMLInputElement>) => {
    groupTitle?.input.onChange(e);
    groupTitle?.setErrors([]);
  };

  const onCloseFaqGroupDetails = () => {
    setDefaultGroup();
    faqGroupDrawer.closeDrawer();
    setFaqGroupId(undefined);
    setSelectedTab(FaqGroupDetailsTabs.General);
  };

  const onUpdateFaqGroup = async (status: FaqGroupStatus) => {
    if (faqGroupData && faqGroupId) {
      return updateFaqGroupDetails({
        faqGroupData: {
          ...faqGroupData,
          title: groupTitleValue,
          status,
          translations: translations ?? {},
        },
        faqGroupId,
      });
    }
  };

  const onCreateFaqGroup = async (status: FaqGroupStatus) => {
    const groupToSubmit: FaqGroupDataToCreate = {
      title: groupTitleValue,
      status,
      order: 0,
      questionsCount: 0,
      translations: translations ?? {},
    };

    return createFaqGroupDetails({ data: groupToSubmit });
  };

  const onSubmitGroup = async (status: FaqGroupStatus) => {
    const isDuplicateGroup = faqGroups?.some(
      (group) => group.title === groupTitleValue && faqGroupId !== group.id,
    );

    if (disabled) {
      return;
    }

    if (isDuplicateGroup) {
      return performGroupTitleError();
    }

    try {
      if (faqGroupId) {
        await onUpdateFaqGroup(status);
      } else {
        await onCreateFaqGroup(status);
      }

      onCloseFaqGroupDetails();
    } catch (error) {
      console.error("Error submitting faq group details:", error);
    }
  };

  const handleSaveDraft = () => onSubmitGroup(FaqGroupStatus.Draft);
  const handleSaveActive = () => onSubmitGroup(FaqGroupStatus.Active);

  const handleSaveTranslations = async () => {
    if (disabled) {
      return;
    }

    if (!faqGroupId) {
      return;
    }

    if (faqGroupData && faqGroupId) {
      await updateFaqGroupDetails({
        faqGroupData: {
          translations: translations ?? {},
        },
        faqGroupId,
      });
    }

    onCloseFaqGroupDetails();
  };

  const isSubmitting = isSubmittingFaqGroup || isPending;

  const value = {
    isLoading,
    onCloseFaqGroupDetails,
    onChangeGroupTitle,
    onSubmitGroup,
    selectedTab,
    setSelectedTab,
    translations,
    setTranslations,
    setErrors,
    groupTitle,
    errors,
    disabled,
    isSubmitting,
    handleSaveDraft,
    handleSaveActive,
    handleSaveTranslations,
  };

  return <Provider value={value}>{children}</Provider>;
};

export { FaqGroupDetailsProvider, useFaqGroupDetails };
